import { useMemo, useState } from "react";
import { liveFeed, type LiveItem } from "@/mocks/liveFeed";
import SectionHeading from "./SectionHeading";

const filters = ["全部", "娱乐", "影视", "科技", "体育", "社会"];

const typeStyle: Record<string, string> = {
  娱乐: "bg-primary-100 text-primary-700",
  影视: "bg-secondary-100 text-secondary-800",
  科技: "bg-accent-100 text-accent-800",
  体育: "bg-foreground-100 text-foreground-800",
  社会: "bg-background-200 text-foreground-700",
};

const statusStyle: Record<LiveItem["status"], string> = {
  已证实: "bg-accent-50 text-accent-800 border-accent-200",
  多方讨论: "bg-secondary-50 text-secondary-800 border-secondary-200",
  待核实: "bg-primary-50 text-primary-700 border-primary-200",
};

const statusIcon: Record<LiveItem["status"], string> = {
  已证实: "ri-checkbox-circle-line",
  多方讨论: "ri-chat-3-line",
  待核实: "ri-error-warning-line",
};

function formatHeat(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)} 万`;
  return value.toString();
}

const PAGE_SIZE = 5;

export default function LiveFeed() {
  const [activeFilter, setActiveFilter] = useState("全部");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    if (activeFilter === "全部") return liveFeed;
    return liveFeed.filter((item) => item.type === activeFilter);
  }, [activeFilter]);

  const visibleItems = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const handleFilter = (value: string) => {
    setActiveFilter(value);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <section id="live" className="scroll-mt-24 w-full bg-background-50">
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="实时快报 · 24 小时滚动更新"
            title="正在发生的动静，按时间线排好"
            desc="每一条快讯都标注了来源可信度：已证实、多方讨论、待核实。顺着时间往下看，你就能把一件事从最早露出到后续发酵走向串起来。"
            tone="primary"
          />
          <div className="shrink-0 flex items-center gap-2 text-xs text-foreground-500">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-primary-400 animate-pulse-dot"></span>
              <span className="relative inline-flex w-2 h-2 rounded-full bg-primary-500"></span>
            </span>
            时间线持续更新中 · 共 {liveFeed.length} 条
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="快讯类型筛选">
          {filters.map((filter) => {
            const active = filter === activeFilter;
            return (
              <button
                key={filter}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handleFilter(filter)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap border ${
                  active
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {filter}
              </button>
            );
          })}
        </div>

        <ol className="relative mt-8">
          <span
            className="absolute left-[7px] top-3 bottom-3 w-px bg-background-200 md:left-[95px]"
            aria-hidden="true"
          ></span>

          {visibleItems.map((item) => (
            <li key={item.id} className="relative pl-7 pb-5 md:pl-[130px] animate-fade-up">
              <span
                className="absolute left-0 top-5 w-[15px] h-[15px] rounded-full bg-primary-500 ring-4 ring-background-50 md:left-[88px]"
                aria-hidden="true"
              ></span>

              <div className="hidden md:block absolute left-0 top-4 w-[76px] text-right">
                <span className="font-heading text-lg text-foreground-900 tabular-nums leading-none">{item.time}</span>
                <span className="block text-[11px] text-foreground-500 mt-1">{item.day}</span>
              </div>

              <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 transition-colors hover:border-primary-300">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      typeStyle[item.type] ?? "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {item.type}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${statusStyle[item.status]}`}
                  >
                    <i className={`${statusIcon[item.status]} text-xs`} aria-hidden="true"></i>
                    {item.status}
                  </span>
                  <span className="md:hidden text-xs text-foreground-500 tabular-nums ml-auto">
                    {item.day} {item.time}
                  </span>
                </div>

                <h3 className="mt-3 font-heading text-lg md:text-xl text-foreground-950 leading-snug">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.summary}</p>

                {item.image && (
                  <div className="mt-4 w-full md:w-[320px] h-[180px] rounded-md overflow-hidden">
                    <img
                      src={item.image}
                      alt={`${item.title}配图`}
                      title={`${item.title} - 瓜田速递实时快报`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 ml-auto text-primary-600">
                    <i className="ri-fire-line text-base" aria-hidden="true"></i>
                    <span className="text-xs font-medium">{formatHeat(item.heat)} 热度</span>
                  </div>
                </div>
              </article>
            </li>
          ))}

          {visibleItems.length === 0 && (
            <li className="pl-7 md:pl-[130px]">
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-8 text-center">
                <i className="ri-inbox-line text-3xl text-foreground-400" aria-hidden="true"></i>
                <p className="mt-3 text-sm text-foreground-600">这个板块暂时还没有新快讯，换个分类看看</p>
              </div>
            </li>
          )}
        </ol>

        <div className="mt-4 pl-7 md:pl-[130px]">
          {hasMore ? (
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-down-line" aria-hidden="true"></i>
              加载更早的快讯
            </button>
          ) : (
            <p className="text-xs text-foreground-500">已经翻到本分类的底部了 · 时间线仍在继续更新</p>
          )}
        </div>
      </div>
    </section>
  );
}